/**
 * Cron Job Dashboard
 *
 * Comprehensive dashboard for monitoring and managing scheduled cron jobs.
 * Displays job status, execution history, health metrics, and allows manual triggers.
 */

import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Activity,
  Play,
  RefreshCw,
  Calendar,
  TrendingUp,
  Server,
  ArrowLeft,
  Loader,
} from "lucide-react";
import toast from "react-hot-toast";
import cronJobsService from "../../services/cronJobs";

const CronJobDashboard: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  // Fetch overview data
  const {
    data: overviewData,
    isLoading: overviewLoading,
    refetch: refetchOverview,
  } = useQuery({
    queryKey: ["cron-jobs-overview"],
    queryFn: cronJobsService.getOverview,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch detailed status
  const {
    data: statusData,
    isLoading: statusLoading,
    refetch: refetchStatus,
  } = useQuery({
    queryKey: ["cron-jobs-status"],
    queryFn: cronJobsService.getAllJobsStatus,
    refetchInterval: 30000,
  });

  // Fetch health report
  const { data: healthData, isLoading: healthLoading } = useQuery({
    queryKey: ["cron-jobs-health"],
    queryFn: cronJobsService.getHealthReport,
    refetchInterval: 30000,
  });

  // Manual trigger mutation
  const triggerMutation = useMutation({
    mutationFn: (jobName: string) => cronJobsService.triggerJob(jobName),
    onSuccess: (data, jobName) => {
      toast.success(`Job "${jobName}" executed successfully!`);
      // Refetch all data
      queryClient.invalidateQueries({ queryKey: ["cron-jobs-overview"] });
      queryClient.invalidateQueries({ queryKey: ["cron-jobs-status"] });
      queryClient.invalidateQueries({ queryKey: ["cron-jobs-health"] });
    },
    onError: (error: any, jobName) => {
      toast.error(
        error?.response?.data?.message || `Failed to execute job "${jobName}"`,
      );
    },
  });

  const isLoading = overviewLoading || statusLoading || healthLoading;
  const overview = overviewData?.data;
  const allJobs = statusData?.data || {};
  const health = healthData?.data;

  // Filter jobs by category
  const filteredJobs =
    selectedCategory === "all"
      ? Object.entries(allJobs)
      : Object.entries(allJobs).filter(
          ([_, job]) => job.metadata.category === selectedCategory,
        );

  // Get health status color
  const getHealthColor = (status?: string) => {
    switch (status) {
      case "healthy":
        return "text-green-600 bg-green-100";
      case "warning":
        return "text-yellow-600 bg-yellow-100";
      case "critical":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getHealthIcon = (status?: string) => {
    switch (status) {
      case "healthy":
        return <CheckCircle className="w-6 h-6" />;
      case "warning":
        return <AlertTriangle className="w-6 h-6" />;
      case "critical":
        return <XCircle className="w-6 h-6" />;
      default:
        return <Activity className="w-6 h-6" />;
    }
  };

  const handleRefresh = () => {
    refetchOverview();
    refetchStatus();
    toast.success("Dashboard refreshed!");
  };

  const handleTriggerJob = (jobName: string) => {
    if (
      confirm(
        `Are you sure you want to manually trigger the job: ${jobName}?\n\nThis will execute the job immediately.`,
      )
    ) {
      triggerMutation.mutate(jobName);
    }
  };

  const formatDateTime = (date: Date | string | null) => {
    if (!date) return "Never";
    const d = new Date(date);
    return d.toLocaleString();
  };

  const getTimeSince = (date: Date | string | null) => {
    if (!date) return "Never";
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMins > 0) return `${diffMins}m ago`;
    return "Just now";
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-6">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-blue-500"></div>
            <p className="text-slate-600 font-medium">
              Loading cron job dashboard...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/superadmin")}
                className="bg-slate-100 hover:bg-slate-200 p-2 rounded-xl transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-slate-700" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                  <Clock className="w-7 h-7 text-blue-500" />
                  Cron Job Monitor
                </h1>
                <p className="text-slate-600 font-medium mt-1">
                  Monitor and manage scheduled background jobs
                </p>
              </div>
            </div>
            <button
              onClick={handleRefresh}
              className="bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
            >
              <RefreshCw className="h-5 w-5 text-blue-500" />
              <span className="text-slate-700">Refresh</span>
            </button>
          </div>
        </div>

        {/* System Health Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-slate-600">
                System Health
              </h3>
              <div
                className={`p-2 rounded-xl ${getHealthColor(overview?.healthStatus)}`}
              >
                {getHealthIcon(overview?.healthStatus)}
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900 capitalize">
              {overview?.healthStatus || "Unknown"}
            </p>
          </div>

          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-slate-600">Total Jobs</h3>
              <div className="bg-blue-100 p-2 rounded-xl">
                <Server className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {overview?.totalJobs || 0}
            </p>
          </div>

          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-slate-600">Active Jobs</h3>
              <div className="bg-green-100 p-2 rounded-xl">
                <Activity className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {overview?.activeJobs || 0}
            </p>
          </div>

          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-slate-600">Alerts</h3>
              <div className="bg-red-100 p-2 rounded-xl">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {overview?.alerts?.length || 0}
            </p>
          </div>
        </div>

        {/* Alerts Section */}
        {overview?.alerts && overview.alerts.length > 0 && (
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-red-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Active Alerts
            </h3>
            <div className="space-y-2">
              {overview.alerts.map((alert, index) => (
                <div
                  key={index}
                  className="bg-white/80 p-4 rounded-xl border border-red-200"
                >
                  <p className="text-red-800 font-medium">{alert}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Category Filter */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4">
            Filter by Category
          </h3>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setSelectedCategory("all")}
              className={`px-4 py-2 rounded-xl font-medium transition-all ${
                selectedCategory === "all"
                  ? "bg-blue-500 text-white shadow-lg"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              All Jobs ({overview?.totalJobs || 0})
            </button>
            {overview?.categories &&
              Object.entries(overview.categories).map(([category, stats]) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-xl font-medium transition-all ${
                    selectedCategory === category
                      ? "bg-blue-500 text-white shadow-lg"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  {category} ({stats.total})
                </button>
              ))}
          </div>
        </div>

        {/* Jobs List */}
        <div className="space-y-4">
          <h3 className="text-xl font-bold text-slate-900">Cron Jobs</h3>
          {filteredJobs.length === 0 ? (
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12 text-center">
              <Server className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 font-medium">
                No jobs found in this category
              </p>
            </div>
          ) : (
            filteredJobs.map(([jobName, job]) => {
              const jobHealth = health?.jobs?.[jobName];
              const isHealthy =
                jobHealth?.health === "good" || jobHealth?.health === "healthy";
              const hasIssues =
                jobHealth?.health === "warning" ||
                jobHealth?.health === "critical" ||
                jobHealth?.health === "stale";

              return (
                <div
                  key={jobName}
                  className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-lg font-bold text-slate-900">
                          {job.metadata.name}
                        </h4>
                        {job.running ? (
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">
                            Running
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-bold">
                            Scheduled
                          </span>
                        )}
                        {isHealthy && (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        )}
                        {hasIssues && (
                          <AlertTriangle className="w-5 h-5 text-yellow-500" />
                        )}
                      </div>
                      <p className="text-slate-600 mb-3">
                        {job.metadata.description}
                      </p>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-600 font-medium">
                            {job.metadata.schedule}
                          </span>
                        </div>
                        <div className="px-3 py-1 bg-blue-50 text-blue-700 rounded-lg font-mono text-xs">
                          {job.metadata.cronPattern}
                        </div>
                        <div className="px-3 py-1 bg-purple-50 text-purple-700 rounded-lg text-xs font-bold">
                          {job.metadata.category}
                        </div>
                      </div>
                    </div>
                    {job.metadata.canManualRun && (
                      <button
                        onClick={() => handleTriggerJob(jobName)}
                        disabled={triggerMutation.isPending}
                        className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-4 py-2 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {triggerMutation.isPending ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                        Run Now
                      </button>
                    )}
                  </div>

                  {/* Job Statistics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-200">
                    <div>
                      <p className="text-xs text-slate-500 font-bold mb-1">
                        Last Run
                      </p>
                      <p className="text-sm font-bold text-slate-900">
                        {getTimeSince(job.history.lastRun)}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatDateTime(job.history.lastRun)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-bold mb-1">
                        Last Success
                      </p>
                      <p className="text-sm font-bold text-green-600">
                        {getTimeSince(job.history.lastSuccess)}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatDateTime(job.history.lastSuccess)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-bold mb-1">
                        Total Failures
                      </p>
                      <p className="text-sm font-bold text-slate-900">
                        {job.history.failures || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-bold mb-1">
                        Consecutive Failures
                      </p>
                      <p
                        className={`text-sm font-bold ${
                          job.history.consecutiveFailures > 0
                            ? "text-red-600"
                            : "text-green-600"
                        }`}
                      >
                        {job.history.consecutiveFailures || 0}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Recent Activity */}
        {overview?.recentActivity && overview.recentActivity.length > 0 && (
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-500" />
              Recent Activity
            </h3>
            <div className="space-y-3">
              {overview.recentActivity.map((activity, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200"
                >
                  <div className="flex items-center gap-3">
                    {activity.success ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                    <div>
                      <p className="font-bold text-slate-900">
                        {activity.name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {activity.jobName}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-slate-600">
                      {getTimeSince(activity.lastRun)}
                    </p>
                    <p className="text-xs text-slate-500">
                      {formatDateTime(activity.lastRun)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CronJobDashboard;
